import React from 'react';
import {trim} from 'lodash';
import {toStringId} from '@packages/systems/core/utils/HtmlUtils';
import {
  Expressions,
  withSystem,
  withMeta,
  Plugins,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditor,
  updateExpressionsAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  TRecord,
  TText,
  TEnum,
  TBoolean,
  TTypeConstructor,
  EText,
} from '@packages/systems/wfdl-ast';
import {
  FORM_NAME_PATH,
  INPUT_TYPE_PATH,
  ATTR_REQUIRED_PATH,
  ATTR_ID_PATH,
  ATTR_NAME_PATH,
  ATTR_VALUE_PATH,
  ATTR_DATA_NAME_PATH,
  RADIO_DEFAULT_VALUE,
  RADIO_DEFAULT_VALUE_CAP,
} from '../constants';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import {getNewElementId} from '@packages/utilities/platform';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';
import {useDesignerSelector} from '@packages/systems/designer/flux';

const {getIn} = ContextualExpressions;

const Grid = emotionStyled('div')({
  display: 'grid',
  gap: tokens.space[2],
});

// @ts-expect-error - TS7031 - Binding element 'DesignerStore' implicitly has an 'any' type.
const selectComponents = ({DesignerStore}) =>
  DesignerStore.stagedComponents || DesignerStore.components;

const fields = {
  form: TRecord({
    type: withSystem({defaultValue: 'radio-input', private: true}, TText),
    name: withSystem(
      {
        defaultValue: RADIO_DEFAULT_VALUE_CAP,
        private: true,
        isLocalizable: true,
      },
      TText
    ),
  }),
  inputType: withSystem(
    {
      defaultValue: 'default',
      label: 'Style',
      isLocalizable: true,
    },
    TEnum({default: 'Default', custom: 'Custom'})
  ),
  attr: TRecord({
    type: withSystem({defaultValue: RADIO_DEFAULT_VALUE, private: true}, TText),
    name: withSystem(
      {defaultValue: RADIO_DEFAULT_VALUE, private: true, isLocalizable: true},
      TText
    ),
    id: withSystem(
      {
        private: true,
        defaultValue: RADIO_DEFAULT_VALUE,
        label: 'ID',
        isBindable: false,
        isLocalizable: true,
      },
      TTypeConstructor(qualify(Plugins.name('Basic'), typeName('IdTextInput')))
    ),
    value: withMeta(
      {placeholder: 'e.g. Blue'},
      withSystem(
        {
          label: 'Choice Value',
          defaultValue: RADIO_DEFAULT_VALUE_CAP,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
    'data-name': withMeta(
      {placeholder: 'e.g. Eye Color'},
      withSystem(
        {
          label: 'Group Name',
          defaultValue: RADIO_DEFAULT_VALUE_CAP,
          isLocalizable: true,
        },
        TTypeConstructor(qualify(Plugins.name('Basic'), typeName('OnBlurText')))
      )
    ),
    required: withSystem(
      {labelVariant: 'hidden', label: 'Required', isLocalizable: true},
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('ReverseLabelBoolean'))
      )
    ),
  }),
  temp: TRecord({
    checkbox: TRecord({
      isChecked: withSystem({defaultValue: false, private: true}, TBoolean),
      isFocused: withSystem({defaultValue: false, private: true}, TBoolean),
      isFocusedVisible: withSystem(
        {defaultValue: false, private: true},
        TBoolean
      ),
    }),
  }),
} as const;

const FormRadioType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: function RenderFormRadioType({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: (arg1?: any) => void;
  }) {
    const componentMap = useDesignerSelector(selectComponents);

    const onChangeAt = React.useCallback(
      (at: ExpressionPath) => extendExpressionEditorActionAtPath(dispatch)(at),
      [dispatch]
    );

    const onChangeName = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'exp' implicitly has an 'any' type.
      (exp) => {
        const newName = Expressions.getText(exp.payload.newExpression) || '';
        const trimmedName = trim(newName);
        const trimmedExp = EText(trimmedName);
        const idString = toStringId(trimmedName) || '';
        dispatch(
          updateExpressionsAction([
            {key: ATTR_DATA_NAME_PATH, newExpression: trimmedExp},
            {key: ATTR_NAME_PATH, newExpression: EText(idString)},
            {key: FORM_NAME_PATH, newExpression: trimmedExp},
          ])
        );
      },
      [dispatch]
    );

    const onChangeValue = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'exp' implicitly has an 'any' type.
      (exp) => {
        const currentNameFromExpr = getIn(expr, ATTR_VALUE_PATH);
        const currentName = currentNameFromExpr
          ? ContextualExpressions.getText(stripAnn(currentNameFromExpr))
          : RADIO_DEFAULT_VALUE;
        const newVal = Expressions.getText(exp.payload.newExpression) || '';
        const trimmedVal = trim(newVal);
        const changes = [
          {key: ATTR_VALUE_PATH, newExpression: EText(trimmedVal)},
        ];
        const idString = toStringId(trimmedVal) || RADIO_DEFAULT_VALUE;
        if (currentName && idString !== toStringId(currentName)) {
          changes.push({
            key: ATTR_ID_PATH,
            newExpression: EText(getNewElementId(componentMap, idString)),
          });
        }
        dispatch(updateExpressionsAction(changes));
      },
      [dispatch, expr, componentMap]
    );

    const expressionEditorAtPath = React.useCallback(
      (path: ExpressionPath, onDispatch: void | ((arg1: any) => void)) => {
        const exprAt = getIn(expr, path);
        if (!exprAt) {
          throw Error(
            `Failed to get expression at path: ${JSON.stringify(path)}`
          );
        }
        return (
          <ExpressionEditor
            expression={exprAt}
            dispatch={onDispatch || onChangeAt(path)}
          />
        );
      },
      [expr, onChangeAt]
    );

    const Name = expressionEditorAtPath(ATTR_DATA_NAME_PATH, onChangeName);
    const InputType = expressionEditorAtPath(INPUT_TYPE_PATH);
    const Required = expressionEditorAtPath(ATTR_REQUIRED_PATH);
    const Value = expressionEditorAtPath(ATTR_VALUE_PATH, onChangeValue);

    return (
      <Grid>
        {Name}
        {Value}
        {InputType}
        {Required}
      </Grid>
    );
  },
});

export default FormRadioType;
